import { itx } from "../../../src/translationItx/itx";
import { Building2, PlugZap, Sofa } from "lucide-react";
import type { ReactNode } from "react";
import { useOnChangeWatcher } from "../../../src/models/useOnChangeWatcher";
import { C1_EventKey } from "../C1_EventKey";
import c1_state from "../model/C1_State";
import type { EditMode } from "../types/C1_AppTypes";

export type C1_EditModeButton = {
  mode: EditMode;
  label: string;
  icon: ReactNode;
  active: boolean;
  select: () => void;
};

type C1EditModeButtonsProps = {
  electricalMode?: "EloConcept" | "EloDetail";
  renderButton: (button: C1_EditModeButton) => ReactNode;
};

/** Applies one C1 editing mode including its mode-specific dock defaults. */
export function selectC1EditMode(mode: EditMode): void {
  // The editor owns the current mode; docking only adjusts the panels afterwards.
  c1_state.editMode.value = mode;
  state.dockManager.selectMode(mode);
  c1_state.eventLogger.addEvent(C1_EventKey.ModeSelected, { mode });
}

/** Provides the shared C1 mode buttons to either menu presentation. */
export function C1_EditModeButtons({ renderButton, electricalMode = "EloConcept" }: C1EditModeButtonsProps) {
  useOnChangeWatcher(c1_state.editMode);
  const definitions: Array<Omit<C1_EditModeButton, "active" | "select">> = [
    { mode: "building", label: itx("c1.ui.Gebaude", "Gebäude"), icon: <Building2 strokeWidth={1.4} /> },
    { mode: "furnishing", label: itx("stepsHeader.defaultFunctions", "Ausstattung"), icon: <Sofa strokeWidth={1.4} /> },
    { mode: electricalMode, label: itx("c1.ui.Elektro", "Elektro"), icon: <PlugZap strokeWidth={1.4} /> },
  ];
  if (electricalMode === "EloConcept") {
    definitions.push({ mode: "interactionConcept", label: itx("c1.ui.Bedienung", "Bedienung"), icon: (
      <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x={4} y={4} width={16} height={16} />
        <path d="M 12 4 v 3 M 12 20 v -3 M 4 12 h 3 M 20 12 h -3" />
      </svg>
    ) });
  }

  const buttons: ReactNode[] = [];
  const currentMode = c1_state.editMode.value;
  const electricalModeActive = currentMode === "EloConcept" || currentMode === "EloDetail";
  for (const definition of definitions) {
    const mode = definition.mode;
    buttons.push(
      renderButton({
        ...definition,
        active: mode === electricalMode ? electricalModeActive : currentMode === mode,
        select: () => selectC1EditMode(mode),
      }),
    );
  }
  return <>{buttons}</>;
}
